import { HubJobKind } from '@robovision/hub'
import {
  NotificationCenterEventClass,
  type NotificationCenterNotification,
} from '@robovision/notification-center'
import {
  Notification,
  NotificationTypes,
} from '@robovision/notifications'
import { capitalize } from 'lodash-es'
import { Dialog } from 'quasar'
import type { Router } from 'vue-router'
import StopJobImportDialog from '~components/dialogs/StopJobImportDialog.vue'
import { ImportExportSubject } from '~domain/importExport/ImportExportSubject'
import { ImportJobStatus } from '~domain/io/ImportJob'
import { NotificationTypeId } from '~domain/notifications/NotificationTypeId'
import { SAM_JOB_IDS } from '~domain/SamTool'
import { i18n } from '~i18n'
import router from '~router'
import { RouteNames } from '~router/routeNames'
import { useProjectsStore } from '~stores/projectsStore/useProjectsStore'
import { useDatapoolsStore } from '~stores/useDatapoolsStore'
import { useImportJobsStore } from '~stores/useImportJobsStore'
import { useInferencesStore } from '~stores/useInferencesStore'
import { useJobsStore } from '~stores/useJobsStore'
import { useSessionStore } from '~stores/useSessionStore'
import { useWorkflowsStore } from '~stores/useWorkflowsStore'
import { useLabelerUserNames } from '../composables/useLabelerUserNames'


type NotificationEventParameters = {
  job_definition_id: string,
  job_id: string,
  project_id: string
  workflow_id: string
}

export async function dataToNotification (notification: NotificationCenterNotification): Promise<Notification> {
  return new Notification({
    action: getNotificationAction(notification),
    created: notification.createdAt,
    id: notification.eventId,
    isRead: notification.read,
    message: await getNotificationMessage(notification),
    progress: notification.progress,
    subject: getNotificationSubject(notification),
    type: getNotificationType(notification),
  })
}

function renderTemplate (template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] ?? match
  })
}

function isSampleAnalysis (notification: NotificationCenterNotification): boolean {
  const sampleAnalysisTypeIds = [NotificationTypeId.CustomJobFailed, NotificationTypeId.CustomJobFinished] as string[]
  if (!sampleAnalysisTypeIds.includes(notification.eventTypeId)) {
    return false
  }

  const {
    job_definition_id: jobDefinitionId,
  } = notification.eventParameters as Record<string, string>
  return !!jobDefinitionId && SAM_JOB_IDS.includes(jobDefinitionId)
}

async function getNotificationMessage (notification: NotificationCenterNotification): Promise<string> {
  const { getProjectByDatapoolId } = useProjectsStore()
  const {
    eventTypeId,
    renderedMessage,
  } = notification

  switch (eventTypeId) {
    case NotificationTypeId.CustomJobFailed:
      if (isSampleAnalysis(notification)) {
        return i18n.t('notifications.messages.sampleAnalysisFailed')
      } else {
        return renderedMessage
      }
    case NotificationTypeId.CustomJobFinished:
      if (isSampleAnalysis(notification)) {
        return i18n.t('notifications.messages.sampleAnalysisFinished')
      } else {
        return renderedMessage
      }
    case NotificationTypeId.CopyAnnotationsDone:
    case NotificationTypeId.CopyAnnotationsFailed:
    case NotificationTypeId.CopyAnnotationsPartiallyDone:
    case NotificationTypeId.CopyAnnotationsProgress: {
      // Copy annotations notifications are coming from the datastore where there
      const { currentUser } = useSessionStore()
      const {
        getUserNameById,
        setUserName,
      } = useLabelerUserNames()
      const { loadDatapoolSources } = useDatapoolsStore()
      const sourceId = notification.eventParameters.source_id as string
      const targetId = notification.eventParameters.target_id as string
      const datapoolId = notification.eventParameters.datapool_id as string

      let sourceUser: string | undefined = currentUser?.id === sourceId
        ? currentUser.email
        : getUserNameById(sourceId)
      let targetUser: string | undefined = currentUser?.id === targetId
        ? currentUser.email
        : getUserNameById(targetId)

      const project = await getProjectByDatapoolId(datapoolId)

      if (!sourceUser || !targetUser) {
        // Copy annotations notifications are coming from the datastore where there
        // is no notion of projects or users. Therefore, we map datapool IDs and
        // source IDs here to project IDs and user IDs and render the message template
        // with this new information rather than using the pre-rendered message.
        if (project) {
          try {
            const sources = await loadDatapoolSources(datapoolId)
            sources.forEach(s => setUserName(s.id, s.name))
          } catch (e) {
            console.log(e)
          }
        }

        if (!targetUser) {
          targetUser = getUserNameById(notification.eventParameters.target_id as string) ?? i18n.t('notifications.messages.params.unknownUser')
        }

        if (!sourceUser) {
          // When there is no source id in the parameters, it means the copy operation
        // didn't have a single source user/model but comes from a manual selection.
          sourceUser = i18n.t('notifications.messages.params.selection')
          if (notification.eventParameters.source_id) {
            sourceUser = getUserNameById(notification.eventParameters.source_id as string) ?? i18n.t('notifications.messages.params.unknownSource')
          }
        }
      }

      const params = {
        ...notification.eventParameters as NotificationEventParameters,
        datapool_id: project?.name ?? i18n.t('notifications.messages.params.unknown'),
        source_id: sourceUser,
        target_id: targetUser,
      }
      return renderTemplate(notification.rawMessage, params)
    }
    case NotificationTypeId.UpdateAnnotationsDone:
    case NotificationTypeId.UpdateAnnotationsFailed:
    case NotificationTypeId.UpdateAnnotationsPartiallyDone:
    case NotificationTypeId.UpdateAnnotationsProgress: {
      const datapoolId = notification.eventParameters.datapool_id as string
      const project = await getProjectByDatapoolId(datapoolId)
      const params = {
        ...notification.eventParameters as NotificationEventParameters,
        datapool_id: project?.name ?? i18n.t('notifications.messages.params.unknown'),
      }
      return renderTemplate(notification.rawMessage, params)
    }
    default:
      return renderedMessage
  }
}

function getNotificationAction (notification: NotificationCenterNotification): Notification['action'] | undefined {
  const {
    checkProjectExists,
    getProject,
  } = useProjectsStore()
  const { checkWorkflowExists } = useWorkflowsStore()
  const { checkJobExists } = useJobsStore()
  const { checkInferenceExists } = useInferencesStore()
  const {
    getProjectByDatapoolId,
    getProjectIdByDatapoolId,
  } = useProjectsStore()

  switch (notification.eventTypeId) {
    case NotificationTypeId.ClassImportSucceeded: {
      async function callback (): Promise<void> {
        await router.push({ name: RouteNames.Classes })
      }

      async function expiredCallback (): Promise<boolean> {
        return false
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('classes.actions.viewImport'),
      }
    }

    case NotificationTypeId.CustomJobFailed:
    case NotificationTypeId.CustomJobFinished: {
      async function callback (): Promise<void> {
        const {
          job_definition_id: jobDefinitionId,
          job_id: jobId,
          project_id: projectId,
        } = notification.eventParameters as NotificationEventParameters
        const project = projectId ? getProject(projectId) ?? await checkProjectExists(projectId) : undefined

        if (project) {
          if (isSampleAnalysis(notification)) {
            await router.push({
              name: RouteNames.ProjectLabeling,
              params: { projectName: project.name },
            })
          } else {
            await router.push({
              name: RouteNames.ProjectJobs,
              params: { projectName: project.name },
              query: {
                jobDefinitionId,
                jobId,
                jobKind: HubJobKind.Custom,
              },
            })
          }
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const {
            job_id: jobId,
            project_id: projectId,
          } = notification.eventParameters as NotificationEventParameters
          const projectExists = await checkProjectExists(projectId)
          if (!projectExists) return true

          const job = await checkJobExists(jobId)
          return !job
        } catch (e) {
          console.error(e)
          return true
        }
      }

      let label: string
      if (isSampleAnalysis(notification)) {
        label = i18n.t('projects.jobs.actions.viewSamples')
      } else if (notification.eventTypeId === NotificationTypeId.CustomJobFinished) {
        label = i18n.t('projects.jobs.actions.viewJob', [
          (notification.eventParameters.job_definition_name as string | undefined)?.toLowerCase(),
        ])
      } else {
        label = i18n.t('projects.jobs.actions.viewDetails')
      }

      return {
        callback,
        expiredCallback,
        label,
      }
    }

    case NotificationTypeId.DataImportFailed:
    case NotificationTypeId.DataImportSucceeded:
    case NotificationTypeId.DataImportStopped:
    case NotificationTypeId.SilencedImportSampleFailures:
    case NotificationTypeId.UnresponsiveJob: {
      async function callback (): Promise<void> {
        try {
          const datapoolId = notification.eventParameters.datapool_id as string
          const project = await getProjectByDatapoolId(datapoolId)

          if (project) {
            await router.push({
              name: RouteNames.ProjectDetails,
              params: { projectName: project.name },
            })
          }
        } catch (e) {
          console.error(e)
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const datapoolId = notification.eventParameters.datapool_id as string
          const project = await getProjectByDatapoolId(datapoolId)

          return !project
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: notification.eventTypeId === NotificationTypeId.DataImportFailed
          ? i18n.t('projects.actions.viewDetails')
          : i18n.t('projects.actions.viewImport'),
      }
    }
    case NotificationTypeId.SampleImportFailed: {
      async function callback (): Promise<void> {
        const { getImportJobByJobId } = useImportJobsStore()
        const jobId = notification.eventParameters.job_id as string
        const datapoolId = notification.eventParameters.datapool_id as string
        const projectId = await getProjectIdByDatapoolId(datapoolId)
        const job = getImportJobByJobId(jobId as string)
        if (projectId && job) {
          Dialog.create({
            component: StopJobImportDialog,
            componentProps: {
              job,
              projectId,
            },
          })
        }
      }

      async function expiredCallback (): Promise<boolean> {
        const { getImportJobByJobId } = useImportJobsStore()
        const { job_id: jobId } = notification.eventParameters
        const job = getImportJobByJobId(jobId as string)
        // Expired when the job doesn't exist or it's not running anymore
        return !job || job.status !== ImportJobStatus.RUNNING
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('labeling.imports.actions.cancel'),
      }
    }

    case NotificationTypeId.InferenceFailed:
    case NotificationTypeId.InferenceInsufficientResources: {
      async function callback (): Promise<void> {
        try {
          const { project_id: projectId } = notification.eventParameters as NotificationEventParameters
          const project = getProject(projectId) ?? await checkProjectExists(projectId)
          const inferenceId = notification.eventParameters.deployment_id as string
          const inference = await checkInferenceExists(inferenceId)

          if (!inference?.definitionId) return

          if (project) {
            await router.push({
              name: RouteNames.ProjectInferenceCenter,
              params: { projectName: project.name },
              query: {
                deploymentDefinitionId: inference.definitionId,
                inferenceId,
              },
            })
          }
        } catch (e) {
          console.error(e)
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const { project_id: projectId } = notification.eventParameters as NotificationEventParameters
          const project = await checkProjectExists(projectId)
          return !project
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('projects.actions.viewDetails'),
      }
    }

    case NotificationTypeId.TestFailed:
    case NotificationTypeId.TestFinished: {
      async function callback (): Promise<void> {
        const {
          project_id: projectId,
          workflow_id: workflowId,
        } = notification.eventParameters as NotificationEventParameters
        const project = getProject(projectId) ?? await checkProjectExists(projectId)

        if (project) {
          await router.push({
            name: RouteNames.ProjectTestCenter,
            params: {
              projectName: project.name,
              testId: workflowId,
            },
          })
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const {
            project_id: projectId,
            workflow_id: workflowId,
          } = notification.eventParameters as NotificationEventParameters
          const projectExists = await checkProjectExists(projectId)
          if (!projectExists) return true

          const workflow = await checkWorkflowExists(workflowId)
          return !workflow
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: notification.eventTypeId === NotificationTypeId.TestFinished
          ? i18n.t('models.actions.viewTest')
          : i18n.t('models.actions.viewDetails'),
      }
    }

    case NotificationTypeId.TrainingFailed:
    case NotificationTypeId.TrainingFinished:
    case NotificationTypeId.TrainingResourceLimitReached: {
      async function callback (): Promise<void> {
        const {
          job_id: jobId,
          project_id: projectId,
        } = notification.eventParameters as NotificationEventParameters
        const project = getProject(projectId) ?? await checkProjectExists(projectId)

        if (project) {
          await router.push({
            name: RouteNames.ProjectModels,
            params: { projectName: project.name },
            query: { model: jobId },
          })
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const {
            job_id: jobId,
            project_id: projectId,
          } = notification.eventParameters as NotificationEventParameters
          const projectExists = await checkProjectExists(projectId)
          if (!projectExists) return true

          const job = await checkJobExists(jobId)
          return !job
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: notification.eventTypeId === NotificationTypeId.TrainingFinished
          ? i18n.t('models.actions.viewTraining')
          : i18n.t('models.actions.viewDetails'),
      }
    }

    case NotificationTypeId.CopyAnnotationsDone:
    case NotificationTypeId.CopyAnnotationsFailed:
    case NotificationTypeId.CopyAnnotationsPartiallyDone:
    case NotificationTypeId.CopyAnnotationsProgress:
    case NotificationTypeId.UpdateAnnotationsDone:
    case NotificationTypeId.UpdateAnnotationsFailed:
    case NotificationTypeId.UpdateAnnotationsPartiallyDone:
    case NotificationTypeId.UpdateAnnotationsProgress: {
      async function callback (): Promise<void> {
        try {
          const datapoolId = notification.eventParameters.datapool_id as string
          const project = await getProjectByDatapoolId(datapoolId)

          if (project) {
            await router.push({
              name: RouteNames.ProjectLabeling,
              params: { projectName: project.name },
            })
          }
        } catch (e) {
          console.error(e)
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const datapoolId = notification.eventParameters.datapool_id as string
          const project = await getProjectByDatapoolId(datapoolId)

          return !project
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('projects.actions.labelCenter'),
      }
    }

    case NotificationTypeId.ExportStarted:
    case NotificationTypeId.ExportFailed:
    case NotificationTypeId.ExportFailedMissingData: {
      switch (notification.eventParameters.subject) {
        case ImportExportSubject.Inference: {
          async function callback (): Promise<void> {
            const inferenceId = notification.eventParameters.subject_id as string
            const inference = await checkInferenceExists(inferenceId)
            if (!inference?.projectId) return

            const project = await checkProjectExists(inference.projectId)
            if (!project) return

            await router.push({
              name: RouteNames.ProjectInferenceCenter,
              params: { projectName: project.name },
              query: {
                deploymentDefinitionId: inference.definitionId,
                inferenceId,
              },
            })
          }

          async function expiredCallback (): Promise<boolean> {
            try {
              const inferenceId = notification.eventParameters.subject_id as string
              const inference = await checkInferenceExists(inferenceId)
              return !inference
            } catch (e) {
              console.error(e)
              return true
            }
          }

          return {
            callback,
            expiredCallback,
            label: notification.eventTypeId === NotificationTypeId.ExportStarted
              ? i18n.t('deployments.actions.viewDetails')
              : i18n.t('deployments.actions.viewInference'),
          }
        }
        case ImportExportSubject.DatapoolRecords: {
          if (notification.eventTypeId !== NotificationTypeId.ExportStarted) return undefined

          async function callback (): Promise<void> {
            const datapoolId = notification.eventParameters.subject_id as string
            const project = await getProjectByDatapoolId(datapoolId)
            if (!project) return

            await router.push({
              name: RouteNames.ProjectLabeling,
              params: { projectName: project.name },
              query: { export: 'true' },
            })
          }

          async function expiredCallback (): Promise<boolean> {
            try {
              const datapoolId = notification.eventParameters.subject_id as string
              const project = await getProjectByDatapoolId(datapoolId)

              return !project
            } catch (e) {
              console.error(e)
              return true
            }
          }

          return {
            callback,
            expiredCallback,
            label: i18n.t('projects.actions.viewDetails'),
          }
        }

        default:
          return undefined
      }
    }

    case NotificationTypeId.ImportStarted: {
      switch (notification.eventParameters.subject) {
        // TODO need to enable action again when BE will send inferenceId as evenParameters
        // because for proper navigation we need to send deploymentDefinitionId and inferenceId
        // as query parameters for router
        // case ImportExportSubject.Inference: {
        //   async function callback (): Promise<void> {
        //     const projectId = notification.eventParameters.subject_id as string
        //     const project = await checkProjectExists(projectId)
        //     if (!project) return

        //     await router.push({
        //       name: RouteNames.ProjectInferenceCenter,
        //       params: { projectName: project.name },
        //     })
        //   }

        //   async function expiredCallback (): Promise<boolean> {
        //     try {
        //       const projectId = notification.eventParameters.subject_id as string
        //       const project = await checkProjectExists(projectId)
        //       return !project
        //     } catch (e) {
        //       console.error(e)
        //       return true
        //     }
        //   }

        //   return {
        //     callback,
        //     expiredCallback,
        //     label: i18n.t('deployments.actions.viewDetails'),
        //   }
        // }
        case ImportExportSubject.DatapoolRecords: {
          async function callback (): Promise<void> {
            const datapoolId = notification.eventParameters.subject_id as string
            const project = await getProjectByDatapoolId(datapoolId)
            if (!project) return

            await router.push({
              name: RouteNames.ProjectLabeling,
              params: { projectName: project.name },
            })
          }

          async function expiredCallback (): Promise<boolean> {
            try {
              const datapoolId = notification.eventParameters.subject_id as string
              const project = await getProjectByDatapoolId(datapoolId)

              return !project
            } catch (e) {
              console.error(e)
              return true
            }
          }

          return {
            callback,
            expiredCallback,
            label: i18n.t('projects.actions.viewDetails'),
          }
        }

        default:
          return undefined
      }
    }

    case NotificationTypeId.RecordingFailed:
    case NotificationTypeId.RecordingFinished: {
      async function callback (): Promise<void> {
        const {
          job_id: jobId,
          project_id: projectId,
        } = notification.eventParameters as NotificationEventParameters
        const project = getProject(projectId) ?? await checkProjectExists(projectId)

        if (project) {
          await router.push({
            name: RouteNames.ProjectRecordingCenter,
            params: { projectName: project.name },
            query: {
              recordingId: jobId,
            },
          })
        }
      }

      async function expiredCallback (): Promise<boolean> {
        try {
          const {
            job_id: jobId,
            project_id: projectId,
          } = notification.eventParameters as NotificationEventParameters
          const projectExists = await checkProjectExists(projectId)
          if (!projectExists) return true

          const job = await checkJobExists(jobId)
          return !job
        } catch (e) {
          console.error(e)
          return true
        }
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('recordings.actions.viewRecording'),
      }
    }

    case NotificationTypeId.StorageAlarm:
    case NotificationTypeId.StorageWarning: {
      async function callback (): Promise<void> {
        await router.push({
          name: RouteNames.Resources,
        })
      }

      async function expiredCallback (): Promise<boolean> {
        return false
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('resources.storage.actions.viewDetails'),
      }
    }

    case NotificationTypeId.PlatformDeploymentPushed: {
      async function callback (): Promise<void> {
        await router.push({
          name: RouteNames.PlatformConnections,
        })
      }

      async function expiredCallback (): Promise<boolean> {
        return false
      }

      return {
        callback,
        expiredCallback,
        label: i18n.t('topology.actions.viewDetails'),
      }
    }

    default:
      return undefined
  }
}

export function getNotificationSubject (notification: NotificationCenterNotification): string {
  const {
    eventParameters,
    eventTypeId,
  } = notification

  switch (eventTypeId) {
    case NotificationTypeId.CopyAnnotationsDone:
      return i18n.t('notifications.subjects.copyAnnotationsDone', [eventParameters.policy_done ?? 'copied'])
    case NotificationTypeId.CopyAnnotationsFailed:
      return i18n.t('notifications.subjects.copyAnnotationsFailed', [eventParameters.policy_action ?? 'copy'])
    case NotificationTypeId.CopyAnnotationsPartiallyDone:
      return i18n.t('notifications.subjects.copyAnnotationsPartiallyDone', [eventParameters.policy_done ?? 'copied'])
    case NotificationTypeId.CopyAnnotationsProgress:
      return i18n.t('notifications.subjects.copyAnnotationsProgress', [eventParameters.policy_title ?? 'Copying'])
    case NotificationTypeId.CustomJobFailed:
      if (isSampleAnalysis(notification)) {
        return i18n.t('notifications.subjects.sampleAnalysisFailed')
      } else {
        return i18n.t('notifications.subjects.customJobFailed', [eventParameters.job_definition_name])
      }
    case NotificationTypeId.CustomJobFinished:
      if (isSampleAnalysis(notification)) {
        return i18n.t('notifications.subjects.sampleAnalysisFinished')
      } else {
        return i18n.t('notifications.subjects.customJobFinished', [eventParameters.job_definition_name])
      }

    case NotificationTypeId.DataImportFailed:
      return eventParameters.recording_id
        ? i18n.t('notifications.subjects.importFailedEdge')
        : i18n.t('notifications.subjects.importFailed')

    case NotificationTypeId.DataImportRunning:
      return eventParameters.recording_id
        ? i18n.t('notifications.subjects.importStartedEdge')
        : i18n.t('notifications.subjects.importStarted')

    case NotificationTypeId.DataImportSucceeded:
      return eventParameters.recording_id
        ? i18n.t('notifications.subjects.importFinishedEdge')
        : i18n.t('notifications.subjects.importFinished')

    case NotificationTypeId.DataImportStopped:
    case NotificationTypeId.DataImportRolledBack:
      return eventParameters.recording_id
        ? i18n.t('notifications.subjects.importStoppedEdge')
        : i18n.t('notifications.subjects.importStopped')

    case NotificationTypeId.ExportFailed:
    case NotificationTypeId.ExportFailedMissingData:
      return i18n.t('notifications.subjects.subjectExport.failed', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ExportFinished:
      return i18n.t('notifications.subjects.subjectExport.finished', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ExportStarted:
      return i18n.t('notifications.subjects.subjectExport.started', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ExportStopped:
      return i18n.t('notifications.subjects.subjectExport.stopped', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ImportFailed:
    case NotificationTypeId.ImportFailedEmptyManifestSelection:
    case NotificationTypeId.ImportFailedIncompatibleAlgorithm:
    case NotificationTypeId.ImportFailedInsufficientStorage:
      return i18n.t('notifications.subjects.subjectImport.failed', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ImportFinished:
      return i18n.t('notifications.subjects.subjectImport.finished', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ImportStarted:
      return i18n.t('notifications.subjects.subjectImport.started', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.ImportStopped:
      return i18n.t('notifications.subjects.subjectImport.stopped', [capitalize(eventParameters.subject_type_name as string)])

    case NotificationTypeId.InferenceInsufficientResources:
      return i18n.t('notifications.subjects.insufficientResources')

    case NotificationTypeId.InferenceResourceLimitReached:
    case NotificationTypeId.TrainingResourceLimitReached:
      return i18n.t('notifications.subjects.resourceLimitReached')

    case NotificationTypeId.BrandAssetsUpdateInvalidJSON:
    case NotificationTypeId.BrandAssetsUpdateFailed:
      return i18n.t('notifications.subjects.brandAssetsUpdateFailed')

    default:
      return eventTypeId
  }
}

export function getNotificationType (notification: NotificationCenterNotification): NotificationTypes {
  switch (notification.eventTypeId) {
    case NotificationTypeId.InferenceInsufficientResources:
      return NotificationTypes.Info
  }

  switch (notification.eventClass) {
    case NotificationCenterEventClass.FAILED:
      return NotificationTypes.Error

    case NotificationCenterEventClass.WARNING:
      return NotificationTypes.Alert

    case NotificationCenterEventClass.SUCCESS:
      return NotificationTypes.Success

    case NotificationCenterEventClass.NEUTRAL:
    default:
      return NotificationTypes.Info
  }
}

export function showPopupNotification (
  notification: NotificationCenterNotification,
  router: Router,
): boolean {
  if (notification.read) return false

  const routeName = router.currentRoute.value.name

  switch (notification.eventTypeId) {
    case NotificationTypeId.CustomJobFailed:
    case NotificationTypeId.CustomJobFinished: {
      if (routeName !== RouteNames.ProjectJobs) return true

      const projectsStore = useProjectsStore()
      const { projectName } = router.currentRoute.value.params
      const { jobDefinitionId: routerJobDefinitionId } = router.currentRoute.value.query
      const {
        job_definition_id: eventJobDefinitionId,
        project_id: projectId,
      } = notification.eventParameters
      const project = projectsStore.getProject(projectId as string)

      return project?.name !== projectName || routerJobDefinitionId !== eventJobDefinitionId
    }

    case NotificationTypeId.DataImportFailed:
    case NotificationTypeId.DataImportSucceeded:
    case NotificationTypeId.DataImportStopped:
    case NotificationTypeId.DataImportRolledBack: {
      if (routeName !== RouteNames.ProjectDetails) return true
      // import from Edge should be shown
      if (notification.eventParameters.recording_id) return true

      const projectsStore = useProjectsStore()
      const { datapool_id: datapoolId } = notification.eventParameters
      const project = projectsStore.getProjectByName(router.currentRoute.value.params.projectName as string)
      const datapools = (project && projectsStore.datapools.get(project.id)) ?? []
      return !datapools.includes(datapoolId as string)
    }

    case NotificationTypeId.DataImportRunning:
      return false

    case NotificationTypeId.InferenceFailed: {
      if (routeName !== RouteNames.ProjectDetails) return true

      const { projectName } = router.currentRoute.value.params
      const { project_name: eventProjectName } = notification.eventParameters

      return projectName !== eventProjectName
    }

    case NotificationTypeId.TrainingFailed:
    case NotificationTypeId.TrainingFinished: {
      if (routeName !== RouteNames.ProjectModels) return true

      const projectsStore = useProjectsStore()
      const { projectName } = router.currentRoute.value.params
      const { project_id: projectId } = notification.eventParameters
      const project = projectsStore.getProject(projectId as string)

      return project?.name !== projectName
    }

    case NotificationTypeId.TestFailed: {
      if (routeName !== RouteNames.ProjectTestCenter) return true

      const { testId } = router.currentRoute.value.params
      const { workflow_id: workflowId } = notification.eventParameters

      return testId !== workflowId
    }
    case NotificationTypeId.TestFinished:
      return false

    case NotificationTypeId.InferenceStopped:
    case NotificationTypeId.InferenceInsufficientResources: {
      return false
    }

    default:
      return true
  }
}
